import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@oxxean/types';
import {
  createInspection,
  deleteInspection,
  fetchInspections,
  updateInspection,
} from '../../lib/api/manual-data';
import { queryKeys } from '../../lib/query-client';
import { errorMessage } from '../../lib/errors';
import { formatDate, formatNumber, toDateInputValue } from '../../lib/format';
import { useAuth } from '../../hooks/useAuth';
import { useCompanyAreas } from '../../hooks/useCompanyAreas';
import { useFilters } from '../../hooks/useFilters';
import { useTableState } from '../../hooks/useTableState';
import { useToasts } from '../../hooks/useToasts';
import type { CatalogOption, InspectionObservation } from '../../types/domain';
import { DataBoundary } from '../../components/DataBoundary';
import { DataTable, type Column } from '../../components/DataTable';
import { ExportMenu } from '../../components/ExportMenu';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { Button, Card, TableSkeleton } from '../../components/primitives';
import { SelectField, TextAreaField, TextField } from '../../components/form';
import { PageHeading } from '../layout/PageHeading';
import { CaseStatusBadge, CatalogOptions, RiskBadge, RiskOptions, StatusOptions } from './common';
import {
  INSPECTION_DEFAULTS,
  inspectionFormSchema,
  toInspectionInput,
  type InspectionFormValues,
} from './schemas';

/** Fiscalizaciones y observaciones de organismos externos (§19). */
export function InspectionsPage(): JSX.Element {
  const { can } = useAuth();
  const { filters, options } = useFilters();
  const { push } = useToasts();
  const queryClient = useQueryClient();
  const table = useTableState({ sortBy: 'inspectedOn', sortDir: 'desc' });

  const [editing, setEditing] = useState<InspectionObservation | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<InspectionObservation | null>(null);

  const canManage = can(PERMISSIONS.INSPECTIONS_MANAGE);

  const listQuery = useQuery({
    queryKey: queryKeys.inspections({ filters, state: table.state }),
    queryFn: () => fetchInspections(filters, table.state),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['inspections'] });
  };

  const saveMutation = useMutation({
    mutationFn: async (values: InspectionFormValues) => {
      const payload = toInspectionInput(values);
      return editing === null ? createInspection(payload) : updateInspection(editing.id, payload);
    },
    onSuccess: () => {
      push({
        kind: 'success',
        title: editing === null ? 'Fiscalización registrada' : 'Fiscalización actualizada',
      });
      setFormOpen(false);
      setEditing(null);
      invalidate();
    },
    onError: (error) => {
      push({ kind: 'error', title: 'No se pudo guardar', message: errorMessage(error) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteInspection(id),
    onSuccess: () => {
      push({ kind: 'success', title: 'Registro eliminado' });
      setPendingDelete(null);
      invalidate();
    },
    onError: (error) => {
      push({ kind: 'error', title: 'No se pudo eliminar', message: errorMessage(error) });
    },
  });

  const columns = useMemo<Array<Column<InspectionObservation>>>(
    () => [
      {
        key: 'inspectedOn',
        header: 'Fecha',
        sortable: true,
        render: (row) => formatDate(row.inspectedOn),
        plain: (row) => row.inspectedOn,
      },
      {
        key: 'companyLabel',
        header: 'Empresa',
        sortable: true,
        render: (row) => row.companyLabel,
        plain: (row) => row.companyLabel,
      },
      {
        key: 'areaLabel',
        header: 'Área',
        sortable: true,
        render: (row) => row.areaLabel,
        plain: (row) => row.areaLabel,
      },
      {
        key: 'authority',
        header: 'Organismo',
        sortable: true,
        render: (row) => row.authority,
        plain: (row) => row.authority,
      },
      {
        key: 'inspectionType',
        header: 'Tipo',
        sortable: true,
        render: (row) => row.inspectionType,
        plain: (row) => row.inspectionType,
      },
      {
        key: 'subject',
        header: 'Materia',
        render: (row) => row.subject,
        plain: (row) => row.subject,
      },
      {
        key: 'observation',
        header: 'Observación',
        render: (row) => (
          <span title={row.observation}>
            {row.observation.length > 80 ? `${row.observation.slice(0, 80)}…` : row.observation}
          </span>
        ),
        plain: (row) => row.observation,
      },
      {
        key: 'risk',
        header: 'Riesgo',
        sortable: true,
        render: (row) => <RiskBadge risk={row.risk} />,
        plain: (row) => row.risk,
      },
      {
        key: 'fineAmount',
        header: 'Multa',
        align: 'right',
        sortable: true,
        render: (row) => (row.fineAmount === null ? '—' : formatNumber(row.fineAmount, 'integer')),
        plain: (row) => row.fineAmount,
      },
      {
        key: 'responsible',
        header: 'Responsable',
        sortable: true,
        render: (row) => row.responsible,
        plain: (row) => row.responsible,
      },
      {
        key: 'dueDate',
        header: 'Fecha comprometida',
        sortable: true,
        render: (row) => formatDate(row.dueDate),
        plain: (row) => row.dueDate,
      },
      {
        key: 'status',
        header: 'Estado',
        sortable: true,
        render: (row) => <CaseStatusBadge status={row.status} />,
        plain: (row) => row.status,
      },
      {
        key: 'closedOn',
        header: 'Cierre',
        sortable: true,
        render: (row) => formatDate(row.closedOn),
        plain: (row) => row.closedOn,
      },
    ],
    [],
  );

  const rows = listQuery.data;

  return (
    <>
      <PageHeading
        title="Fiscalizaciones"
        description="Fiscalizaciones y observaciones de organismos externos, con la acción comprometida y su plazo. Una fiscalización registrada activa la regla de hallazgo correspondiente."
        actions={
          <>
            <ExportMenu moduleKey="inspections" />
            {canManage && (
              <Button
                variant="primary"
                icon="plus"
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
              >
                Nueva fiscalización
              </Button>
            )}
          </>
        }
      />

      <Card title="Fiscalizaciones registradas" flush>
        <DataBoundary
          isLoading={listQuery.isLoading}
          isError={listQuery.isError}
          error={listQuery.error}
          onRetry={() => void listQuery.refetch()}
          isEmpty={rows !== undefined && rows.total === 0 && table.state.search.length === 0}
          empty={{
            title: 'Sin fiscalizaciones registradas',
            message:
              'No hay fiscalizaciones para los filtros aplicados. La ausencia de registros no equivale a ausencia de fiscalizaciones: verifique que se estén ingresando.',
          }}
          skeleton={<TableSkeleton columns={9} />}
        >
          {rows !== undefined && (
            <DataTable
              caption="Fiscalizaciones y observaciones"
              columns={columns}
              rows={rows.data}
              getRowId={(row) => row.id}
              state={table.state}
              onStateChange={table.setState}
              total={rows.total}
              totalPages={rows.totalPages}
              searchPlaceholder="Buscar por organismo, materia o responsable…"
              isFetching={listQuery.isFetching}
              rowActions={
                canManage
                  ? (row) => (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          icon="edit"
                          onClick={() => {
                            setEditing(row);
                            setFormOpen(true);
                          }}
                        >
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          icon="trash"
                          onClick={() => setPendingDelete(row)}
                          aria-label="Eliminar fiscalización"
                        />
                      </>
                    )
                  : undefined
              }
            />
          )}
        </DataBoundary>
      </Card>

      {formOpen && (
        <InspectionForm
          editing={editing}
          companies={options?.companies}
          areasByCompany={options?.areasByCompany}
          saving={saveMutation.isPending}
          onCancel={() => {
            setFormOpen(false);
            setEditing(null);
          }}
          onSubmit={(values) => saveMutation.mutate(values)}
        />
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Eliminar fiscalización"
        destructive
        confirmLabel="Eliminar"
        loading={deleteMutation.isPending}
        message="Esta acción elimina el registro y no se puede deshacer. Queda constancia en la auditoría."
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete !== null) deleteMutation.mutate(pendingDelete.id);
        }}
      />
    </>
  );
}

interface FormProps {
  editing: InspectionObservation | null;
  companies: CatalogOption[] | undefined;
  /** Todas las áreas con su `companyId`: el formulario encadena por empresa. */
  areasByCompany: CatalogOption[] | undefined;
  saving: boolean;
  onCancel: () => void;
  onSubmit: (values: InspectionFormValues) => void;
}

function InspectionForm({
  editing,
  companies,
  areasByCompany,
  saving,
  onCancel,
  onSubmit,
}: FormProps): JSX.Element {
  const defaults: InspectionFormValues =
    editing === null
      ? INSPECTION_DEFAULTS
      : {
          companyId: editing.companyId ?? '',
          areaId: editing.areaId,
          authority: editing.authority,
          inspectionType: editing.inspectionType,
          inspectedOn: toDateInputValue(editing.inspectedOn),
          subject: editing.subject,
          observation: editing.observation,
          risk: editing.risk,
          responsible: editing.responsible,
          action: editing.action,
          dueDate: toDateInputValue(editing.dueDate),
          status: editing.status,
          fineAmount: editing.fineAmount === null ? '' : String(editing.fineAmount),
          evidenceReference: editing.evidenceReference ?? '',
          closedOn: toDateInputValue(editing.closedOn),
        };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<InspectionFormValues>({
    resolver: zodResolver(inspectionFormSchema),
    defaultValues: defaults,
  });

  const { areas, disabled: areaDisabled, hint: areaHint } = useCompanyAreas({
    areasByCompany,
    companyId: watch('companyId'),
    areaId: watch('areaId'),
    onAreaCleared: () => setValue('areaId', ''),
  });

  return (
    <Modal
      open
      size="wide"
      title={editing === null ? 'Nueva fiscalización' : 'Editar fiscalización'}
      onClose={onCancel}
      footer={
        <>
          <Button onClick={onCancel} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" loading={saving} onClick={() => void handleSubmit(onSubmit)()}>
            Guardar
          </Button>
        </>
      }
    >
      <form className="form-grid" onSubmit={(event) => void handleSubmit(onSubmit)(event)} noValidate>
        <SelectField label="Empresa" error={errors.companyId?.message} {...register('companyId')}>
          <CatalogOptions options={companies} placeholder="Seleccione…" />
        </SelectField>

        <SelectField
          label="Área"
          disabled={areaDisabled}
          hint={areaHint}
          error={errors.areaId?.message}
          {...register('areaId')}
        >
          <CatalogOptions
            options={areas}
            placeholder={areaDisabled ? 'Elija la empresa primero…' : 'Seleccione…'}
          />
        </SelectField>

        <TextField
          label="Organismo fiscalizador"
          required
          hint="Por ejemplo: Dirección del Trabajo, SEREMI de Salud, mutualidad."
          error={errors.authority?.message}
          {...register('authority')}
        />

        <TextField
          label="Tipo de fiscalización"
          required
          error={errors.inspectionType?.message}
          {...register('inspectionType')}
        />

        <TextField
          label="Fecha de fiscalización"
          type="date"
          required
          error={errors.inspectedOn?.message}
          {...register('inspectedOn')}
        />

        <TextField
          label="Materia"
          required
          error={errors.subject?.message}
          {...register('subject')}
        />

        <TextAreaField
          label="Observación"
          required
          full
          error={errors.observation?.message}
          {...register('observation')}
        />

        <SelectField label="Riesgo" required error={errors.risk?.message} {...register('risk')}>
          <RiskOptions />
        </SelectField>

        <TextField
          label="Multa (pesos)"
          type="number"
          min={0}
          step={1}
          hint="Déjelo vacío si no hubo multa. Vacío no equivale a cero."
          error={errors.fineAmount?.message}
          {...register('fineAmount')}
        />

        <TextField
          label="Responsable"
          required
          error={errors.responsible?.message}
          {...register('responsible')}
        />

        <TextAreaField
          label="Acción comprometida"
          required
          full
          error={errors.action?.message}
          {...register('action')}
        />

        <TextField
          label="Fecha comprometida"
          type="date"
          error={errors.dueDate?.message}
          {...register('dueDate')}
        />

        <SelectField label="Estado" required error={errors.status?.message} {...register('status')}>
          <StatusOptions />
        </SelectField>

        <TextField
          label="Fecha de cierre"
          type="date"
          hint="Obligatoria cuando el estado es «Cerrado»."
          error={errors.closedOn?.message}
          {...register('closedOn')}
        />

        <TextField
          label="Evidencia"
          hint="Referencia del acta o expediente de respaldo."
          error={errors.evidenceReference?.message}
          {...register('evidenceReference')}
        />
      </form>
    </Modal>
  );
}
